import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HandCoins, AlertTriangle, Clock, CalendarX, CheckCircle2, Hourglass } from "lucide-react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";

/**
 * 2026-10-19 (user request "KLAIM RETENSI MENUNGGU PEMBAYARAN"): widget Dasbor
 * 6 angka ringkas, semuanya clickable → Collection dengan sub-filter sesuai.
 * SATU sumber data: GET /api/ar/retention-claim-widget (engine A.R.) — angka
 * identik dengan Collection, Ringkasan Proyek, dan Monitoring Proyek.
 * Daftar baris 11 kolom sengaja HANYA di Collection (keputusan user) agar
 * Dasbor tidak padat dan tidak ada dua versi daftar.
 */
export default function RetentionClaimStatusWidget() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ar/retention-claim-widget")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const t = data?.totals;
  const tiles = [
    { key: "belum", label: "Belum Dibayar", icon: Hourglass, count: t?.count_belum_dibayar ?? 0, status: "claim_belum_dibayar", cls: "border-amber-300 text-amber-800" },
    { key: "sebagian", label: "Dibayar Sebagian", icon: Clock, count: t?.count_dibayar_sebagian ?? 0, status: "claim_dibayar_sebagian", cls: "border-indigo-300 text-indigo-800" },
    { key: "lewat", label: "Lewat Jatuh Tempo", icon: AlertTriangle, count: t?.count_lewat_jatuh_tempo ?? 0, status: "claim_lewat_jt", cls: "border-rose-300 text-rose-800" },
    { key: "nodue", label: "Jatuh Tempo Belum Diisi", icon: CalendarX, count: t?.count_jatuh_tempo_belum_diisi ?? 0, status: "claim_no_due", cls: "border-orange-300 text-orange-800" },
    { key: "verif", label: "Perlu Verifikasi", icon: AlertTriangle, count: t?.count_perlu_verifikasi ?? 0, status: "claim_perlu_verifikasi", href: "/collection?tab=incomplete&sub=retention", cls: "border-amber-400 text-amber-900 bg-amber-50/60" },
    { key: "lunas", label: "Lunas", icon: CheckCircle2, count: t?.count_lunas ?? 0, status: "claim_lunas", cls: "border-emerald-300 text-emerald-800" },
  ];

  return (
    <section className="bg-white border border-slate-200" data-testid="section-retention-claim-status">
      <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-100 flex items-center justify-center">
            <HandCoins size={16} className="text-purple-700" />
          </div>
          <div>
            <h3 className="font-display tracking-tight text-[#0B1D3A]">Klaim Retensi Menunggu Pembayaran</h3>
            <p className="text-[10px] text-slate-500">
              Sumber: engine Rekonsiliasi A.R. · acuan {data?.as_of || "—"} · angka identik dengan Collection & Monitoring
            </p>
          </div>
        </div>
        {/* Total sisa klaim belum diterima — clickable → HANYA klaim sisa>0
            (keputusan user closeout 2026-10-19: anti arahkan ke daftar umum
            yang berisi invoice sumber / retensi masih ditahan / non-klaim). */}
        <button type="button" data-testid="claim-widget-total"
          onClick={() => nav("/collection?status=claim_remaining_only")}
          title="Total sisa klaim yang belum diterima — hanya baris klaim retensi dengan sisa > 0"
          className="text-right tabular-nums hover:bg-purple-50 px-2 py-1 transition-colors">
          <div className="overline text-slate-500 text-[9px]">Total Sisa Klaim · Sisa &gt; 0</div>
          <div className="font-display text-lg text-purple-800" data-testid="claim-widget-total-value">
            {loading ? "…" : formatIDR(t?.total_sisa_belum_diterima ?? 0)}
          </div>
        </button>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-slate-100" data-testid="claim-widget-tiles">
        {tiles.map((it) => {
          // 2026-10-19 closeout: kartu Perlu Verifikasi = status kualitas data,
          // bukan pembayaran. Tampilkan TRIPLE terpisah (count + net + sisa)
          // supaya tidak ambigu "angka apa yang dimaksud".
          // 2026-10-20 (user request): kartu Lunas tampilkan "N klaim ·
          // diterima Rp…" (dari pembayaran khusus klaim saja). Perlu
          // Verifikasi & Lunas boleh beririsan.
          const isVerif = it.key === "verif";
          const isLunas = it.key === "lunas";
          return (
            <button key={it.key} type="button"
              data-testid={`claim-widget-${it.key}`}
              onClick={() => nav(it.href || `/collection?status=${it.status}`)}
              title={isVerif
                ? `Perlu Verifikasi: ${it.count} klaim · nilai neto ${formatIDR(t?.verifikasi_net_total ?? 0)} · sisa belum diterima ${formatIDR(t?.verifikasi_remaining_total ?? 0)}`
                : isLunas
                  ? `Lunas: ${it.count} klaim · diterima ${formatIDR(t?.received_from_paid_claims ?? 0)} (hanya pembayaran khusus klaim retensi; pembayaran invoice sumber tidak dihitung)`
                  : `${it.label}: ${it.count} klaim — klik untuk membuka daftar pembentuknya di Collection`}
              className={`bg-white border-l-4 border ${it.cls} px-3 py-2 text-left hover:bg-slate-50 transition-colors`}>
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-semibold opacity-80">
                <it.icon size={10} /> {it.label}
              </div>
              <div className="text-lg font-bold tabular-nums leading-tight">{it.count} klaim</div>
              {isVerif && it.count > 0 && (
                <div className="mt-0.5 space-y-0.5 text-[9px] tabular-nums leading-tight" data-testid="claim-widget-verif-triple">
                  <div><span className="opacity-70">Nilai neto:</span> <b>{formatIDR(t?.verifikasi_net_total ?? 0)}</b></div>
                  <div><span className="opacity-70">Sisa belum diterima:</span> <b>{formatIDR(t?.verifikasi_remaining_total ?? 0)}</b></div>
                </div>
              )}
              {isLunas && (
                <div className="mt-0.5 text-[9px] tabular-nums leading-tight" data-testid="claim-widget-lunas-received">
                  <span className="opacity-70">Diterima:</span> <b>{formatIDR(t?.received_from_paid_claims ?? 0)}</b>
                </div>
              )}
            </button>
          );
        })}
      </div>
      {(t?.overpaid_excess_total ?? 0) > 0 && (
        <div className="px-4 py-2 bg-rose-50 border-t border-rose-200 text-[10px] text-rose-800" data-testid="claim-widget-overpaid-note">
          Kelebihan pembayaran terpisah: <b>{formatIDR(t.overpaid_excess_total)}</b> — ditandai Perlu Verifikasi, tidak disembunyikan dari perhitungan.
        </div>
      )}
    </section>
  );
}
